import {
    collection, doc, getDoc, getDocs, limit, orderBy, query, startAfter, where,
} from "firebase/firestore";
import type { Firestore, Query, QueryDocumentSnapshot } from "firebase/firestore";
import type { Customer, KYC, Service } from "../types";

const PAGE_SIZE = 100;
const READ_CONCURRENCY = 8;

type ExportRecord = {
    service: Service;
    customerData: Customer | undefined;
    kycData: KYC | undefined;
};

export async function* loadServiceExportRecords(
    db: Firestore,
    servicesQuery: Query,
    { includeKyc }: { includeKyc: boolean },
): AsyncGenerator<ExportRecord> {
    let cursor: QueryDocumentSnapshot | undefined;

    while (true) {
        const pageQuery = cursor
            ? query(servicesQuery, startAfter(cursor), limit(PAGE_SIZE))
            : query(servicesQuery, limit(PAGE_SIZE));
        const page = await getDocs(pageQuery);
        if (page.empty) return;

        // Keep caches page-scoped so large exports release full documents as they go.
        const customers = new Map<string, Promise<Customer | undefined>>();
        const kycs = new Map<string, Promise<KYC | undefined>>();
        const records = new Array<ExportRecord>(page.docs.length);
        let nextIndex = 0;
        let failed = false;

        const worker = async () => {
            while (!failed && nextIndex < page.docs.length) {
                const index = nextIndex++;
                const service = page.docs[index].data() as Service;
                const customerID = service.customerObj?.id;
                let customerData: Customer | undefined;
                let kycData: KYC | undefined;

                try {
                    if (customerID) {
                        const customerRef = doc(db, "customers", customerID);
                        let customer = customers.get(customerID);
                        if (!customer) {
                            customer = getDoc(customerRef).then((snapshot) => snapshot.data() as Customer | undefined);
                            customers.set(customerID, customer);
                        }
                        customerData = await customer;

                        if (includeKyc && !failed) {
                            const kycCollection = collection(customerRef, "kyc");
                            const key = JSON.stringify([customerID, service.kyc_id || null]);
                            let kyc = kycs.get(key);
                            if (!kyc) {
                                kyc = service.kyc_id
                                    ? getDoc(doc(kycCollection, service.kyc_id)).then((snapshot) => snapshot.data() as KYC | undefined)
                                    : getDocs(query(kycCollection, orderBy("created", "desc"), where("state.status_code", "==", "valid"), limit(1)))
                                        .then((snapshot) => snapshot.docs[0]?.data() as KYC | undefined);
                                kycs.set(key, kyc);
                            }
                            kycData = await kyc;
                        }
                    }
                    records[index] = { service, customerData, kycData };
                } catch (error) {
                    failed = true;
                    throw error;
                }
            }
        };

        // Each worker awaits one read at a time, including latest-valid-KYC queries.
        await Promise.all(Array.from({ length: Math.min(READ_CONCURRENCY, page.docs.length) }, worker));
        yield* records;

        if (page.docs.length < PAGE_SIZE) return;
        // A snapshot cursor also disambiguates services with the same created timestamp.
        cursor = page.docs[page.docs.length - 1];
    }
}
